import React, { useReducer, useState, useCallback, useEffect, Fragment } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Form,
    FormGroup,
    Label,
    Input,
    Button
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { MdAdd, MdDelete } from "react-icons/md";
// helpers
import handleReducer, { textChange, onBlur, fileChange, formData } from "../lib/handleChange";
import toastAlert from "../lib/toastAlert";
import isEmpty from "../lib/isEmpty";
import { slugify } from "../lib/common";
import fileObjectUrl from "../lib/fileOblectURL";

// api
import { createWork } from "../api/admin";
// validation
import { addWorkValid } from "../validation/admin.validation";
// components
import ButtonLoader from "../mycomponents/Layout/ButtonLoader";
import FieldError from "../mycomponents/Layout/FieldError";
import CKEditor from "../mycomponents/CkEditor";

const initialFormValue = {
    name: "",
    slug: "",
    location: "",
    year: "",
    services: "",
    category: "",
    content: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    coverImage: null,
    midImage: null,
    bottomImages: null
};

const WorkAdd = () => {
    const navigate = useNavigate();

    const [formValue, setFormValue] = useReducer(handleReducer, initialFormValue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loader, setLoader] = useState(false);

    const {
        name,
        slug,
        location,
        year,
        services,
        category,
        content,
        metaTitle,
        metaDescription,
        metaKeywords,
        coverImage,
        midImage,
        bottomImages
    } = formValue;

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "name") {
            textChange(e, setFormValue);
            return setFormValue({ type: "CHANGE", name: "slug", value: slugify(value) });
        } else return textChange(e, setFormValue);
    };

    const handleFileChange = (e) => fileChange(e, setFormValue);

    const handleMultiFileChange = (e) => {
        const files = Array.from(e.target.files);
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp'];
        const invalidFiles = files.filter(file => {
            const ext = file.name.split('.').pop().toLowerCase();
            return !allowedExtensions.includes(ext);
        });

        if (invalidFiles.length > 0) return toastAlert("error", "Only JPG, PNG, WEBP files are allowed!");

        const maxSize = 5 * 1024 * 1024; // 5 MB
        const oversized = files.filter(file => file.size > maxSize);
        if (oversized.length > 0) {
            return toastAlert("error", "Each file must be less than 5MB");
        }

        return setFormValue({ type: "CHANGE", name: "bottomImages", value: files });
    }

    const handleBlur = (e) => {
        const { name } = e.target;
        onBlur(e, setTouched)
    };

    const handleContentChange = (event) => {
        const htmlContent = event.editor.getData();
        setFormValue({ type: "CHANGE", name: "content", value: htmlContent });
    };

    const handleContentBlur = () => {
        setTouched({ ...touched, content: true });
    };

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            try {
                setLoader(true);
                const data = formData(formValue);
                formValue.bottomImages.forEach(file => {
                    data.append("bottomImages", file);
                });
                const { status: ok, message, errors } = await createWork(data);
                setLoader(false);

                if (ok === true) {
                    toastAlert("success", message, "createWork");
                    setFormValue({ type: "RESET", value: initialFormValue });
                    navigate("/project-list");
                } else if (errors) {
                    setErrors(errors);
                } else if (message) {
                    toastAlert("error", message, "createWork");
                }
            } catch (err) {
                console.log(err, "errrr");
                setLoader(false);
            }
        },
        [formValue, navigate]
    );

    useEffect(() => {
        const { status, errors } = addWorkValid(formValue);
        if (!status) setErrors(errors);
        else setErrors({});
    }, [formValue]);

    // console.log(formValue, 'formValueformValue')
    // console.log(errors, 'errorserrorserrors')
    // console.log(touched, 'touchedtouchedtouched')
    return (
        <Row>
            <Col xl={12}>
                <Card>
                    <CardHeader>Add Project</CardHeader>
                    <CardBody>
                        <Form>
                            <Row>
                                {/* Left column */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Project Name</Label>
                                        <Input
                                            type="text"
                                            name="name"
                                            value={name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter name"
                                        />
                                        <FieldError name="name" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Location</Label>
                                        <Input
                                            type="text"
                                            name="location"
                                            value={location}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="e.g Egattur, Chennai"
                                        />
                                        <FieldError name="location" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Category</Label>
                                        <Input
                                            type="text"
                                            name="category"
                                            value={category}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter category"
                                        />
                                        <FieldError name="category" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Year</Label>
                                        <Input
                                            type="number"
                                            name="year"
                                            value={year}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter year"
                                            min={2000}
                                        />
                                        <FieldError name="year" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Services (comma separated)</Label>
                                        <Input
                                            type="textarea"
                                            name="services"
                                            value={services}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="e.g. Interior Design, Decoration"
                                            rows={2}
                                        />
                                        <FieldError name="services" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Mid Image</Label>
                                        <Input
                                            type="file"
                                            name="midImage"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            onBlur={handleBlur}
                                        />
                                        {
                                            midImage && (
                                                <img
                                                    src={fileObjectUrl(midImage)}
                                                    width="140px"
                                                    alt="preview"
                                                    className="mt-2"
                                                />
                                            )
                                        }
                                        <FieldError name="midImage" touched={touched} errors={errors} />
                                    </FormGroup>
                                    ⚠️ Note: Allowed formats: JPG, JPEG, WEBP, PNG. Max size: 5MB.
                                </Col>

                                {/* Right column */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Meta Title</Label>
                                        <Input
                                            type="text"
                                            name="metaTitle"
                                            value={metaTitle}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="SEO title"
                                        />
                                        <FieldError name="metaTitle" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Meta Description</Label>
                                        <Input
                                            type="textarea"
                                            name="metaDescription"
                                            value={metaDescription}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="SEO description"
                                            rows={4}
                                        />
                                        <FieldError
                                            name="metaDescription"
                                            touched={touched}
                                            errors={errors}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Meta Keywords (comma separated)</Label>
                                        <Input
                                            type="textarea"
                                            name="metaKeywords"
                                            value={metaKeywords}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="e.g. keyword1, keyword2"
                                            rows={4}
                                        />
                                        <FieldError name="metaKeywords" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Cover Image</Label>
                                        <Input
                                            type="file"
                                            name="coverImage"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            onBlur={handleBlur}
                                        />
                                        {
                                            coverImage && (
                                                <img
                                                    src={fileObjectUrl(coverImage)}
                                                    width="140px"
                                                    alt="preview"
                                                    className="mt-2"
                                                />
                                            )
                                        }
                                        <FieldError name="coverImage" touched={touched} errors={errors} />
                                    </FormGroup>
                                    ⚠️ Note: Allowed formats: JPG, JPEG, WEBP, PNG. Max size: 5MB.

                                    <FormGroup>
                                        <Label>Bottom Images</Label>
                                        <Input
                                            type="file"
                                            name="bottomImages"
                                            accept="image/*"
                                            multiple
                                            onChange={handleMultiFileChange}
                                            onBlur={handleBlur}
                                        />
                                        {
                                            bottomImages?.length > 0 && bottomImages?.map((file, key) => {
                                                return (
                                                    <img
                                                        src={fileObjectUrl(file)}
                                                        width="140px"
                                                        alt="preview"
                                                        className="mt-2"
                                                    />
                                                )
                                            })
                                        }
                                        <FieldError name="bottomImages" touched={touched} errors={errors} />
                                    </FormGroup>
                                    ⚠️ Note: Allowed formats: JPG, JPEG, WEBP, PNG. Max size: 5MB.
                                </Col>
                            </Row>
                            {/* CKEditor Content Section */}
                            <Row>
                                <Col xs={12}>
                                    <FormGroup>
                                        <Label htmlFor="blogContent">Content *</Label>
                                        <CKEditor
                                            value={content}
                                            onChange={handleContentChange}
                                            onBlur={handleContentBlur}
                                        />
                                        <FieldError name="content" touched={touched} errors={errors} />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <ButtonLoader
                                loader={loader}
                                color="primary"
                                text="Submit"
                                onClick={handleSubmit}
                                disabled={!isEmpty(errors)}
                            />
                        </Form>
                    </CardBody>
                </Card>
            </Col>
        </Row >
    );
};

export default WorkAdd;